import React from 'react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import {
  addErrorToReport,
  loadLanguagePack,
} from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { combineReducers } from 'redux-immutable';
import { OneFetchyeProvider, OneCache } from 'fetchye-one-app';
import { composeModules, RenderModule } from 'holocron';
import {
  experimentConfigReducer,
  prefetchExperimentsV2,
} from '@americanexpress/one-xp-client-sdk';
import SharedScript from '@americanexpress/shared-scripts';
import { useSelector } from 'react-redux';
import childRoutes from '@/childRoutes';
import {
  loadOneCmsLocaleCommonContent,
  loadOneCmsLocaleProductContent,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlFetchyeHooks';
import { routeParamsContext } from '@/context/routeParamsContext';
import { queryParamsContext } from '@/context/queryParamsContext';
import { ModalProvider } from '@/providers/ModalProvider';
import { loadOneCmsLocaleAirports } from '@/hooks/airportHooks/airportsFetchyeHooks';
import { authReducer } from '@/duck/authDuck';
import { useCenturionForcedLoginRedirection } from '@/hooks/forcedLoginHooks';
import CustomTrackProvider from '@/providers/CustomTrackProvider';
import { loungeReducer } from '@/duck/loungeDuck';
import { walletReducer } from '@/duck/walletDuck';
import { isAuthenticatedAndLoadWallet } from '@/thunks/checkIsLoggedInThunk';
import { getNewDomainUrl } from '@/selectors/configSelectors';
import ScrollToTopOnLocationChange from './common/ScrollToTopOnLocationChange';
import LoungeFinderRequiredDataProvider from './LoungeFinderRequiredDataProvider';
import { LoungeFinderHelmets } from './common/LoungeFinderHelmets';
import ErrorReporter from './ErrorReporter';
import AnalyticsWrapper from './AnalyticsWrapper';
import GlobalHeader from './common/GlobalHeader';
// import mockData from '@/mockData'; // uncomment to use mock data
import Footer from './common/Footer';
import LoungeFinderErrorBoundary from './LoungeFinderErrorBoundary';
import TopBanner from './common/TopBanner';
import RootModal from './common/modals/RootModal';
import './common/GlobalOneNavOverrides.scss';

if (global.BROWSER) {
  window.useNativeIntl = true;
}

const TravelLoungeFinderRoot = ({
  languageData: { seoFallbacks },
  locale,
  children: routeChildren,
  params,
  location,
}) => {
  const memoizedRouteParams = React.useMemo(
    () => ({
      ...params,
      loungeId: params?.loungeId?.split(/-+/).pop(),
      pathname: location?.pathname,
      locale: params?.locale,
    }),
    [params, location]
  );
  const domain = useSelector(getNewDomainUrl);
  const pathName = location?.pathname ?? '/';
  const url = new URL(pathName, domain);
  const fullUrl = `${url.toString()}${location?.search}`;

  useCenturionForcedLoginRedirection({
    params,
    locale,
    location,
  });
  const bannerConfig = {
    header: 'LoungeBuddy is no longer available as of January 30, 2025',
    content:
      "Welcome to Lounge Finder! You've been redirected from LoungeBuddy to the American Express Lounge Finder, where eligible American Express Card Members can find details on airport lounges they can access with select American Express Card Products.",
  };

  return (
    <ErrorReporter>
      <OneFetchyeProvider>
        <queryParamsContext.Provider value={location?.search}>
          <routeParamsContext.Provider value={memoizedRouteParams}>
            <ModalProvider>
              <IntlProvider locale={locale}>
                <AnalyticsWrapper>
                  <SharedScript
                    name="one-identity-session"
                    version="^1.0.0"
                    windowCheck="AmexSession"
                  />
                  <LoungeFinderHelmets location={location} />
                  <RenderModule
                    moduleName="axp-one-seo"
                    props={{ fallbacks: seoFallbacks }}
                  />
                  <ScrollToTopOnLocationChange />
                  <CustomTrackProvider>
                    <RenderModule
                      moduleName="axp-global-header"
                      props={{
                        loginDestPage: fullUrl,
                        logoutDestPage: fullUrl,
                      }}
                    />
                    <LoungeFinderErrorBoundary>
                      <LoungeFinderRequiredDataProvider>
                        <TopBanner {...bannerConfig} />
                        <GlobalHeader pathname={location?.pathname} />
                        {routeChildren}
                        <Footer />
                        <RootModal />
                      </LoungeFinderRequiredDataProvider>
                    </LoungeFinderErrorBoundary>
                    <RenderModule moduleName="axp-footer" />
                  </CustomTrackProvider>
                </AnalyticsWrapper>
              </IntlProvider>
            </ModalProvider>
          </routeParamsContext.Provider>
        </queryParamsContext.Provider>
      </OneFetchyeProvider>
    </ErrorReporter>
  );
};

TravelLoungeFinderRoot.propTypes = {
  languageData: PropTypes.shape({
    seoFallbacks: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      openGraph: PropTypes.shape({
        type: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
    }),
  }).isRequired,
  locale: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  params: PropTypes.shape({
    product: PropTypes.string,
    loungeId: PropTypes.string,
    locale: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
};

export const TestableTravelLoungeFinderRoot = TravelLoungeFinderRoot;

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
TravelLoungeFinderRoot.childRoutes = childRoutes;

// uncomment the following line to enable mock data
// global.mock = mockData;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- require needs to be inside browser check
  TravelLoungeFinderRoot.appConfig = require('../appConfig').default;
}
// https://one-app.aexp.com/docs/documentation/how-tos/performance-optimization/server-tracing
export const loadModuleData = async ({
  store: { dispatch, getState },
  ownProps,
  fetchClient,
}) => {
  if (global.BROWSER) {
    // Expose state to window object for debugging
    global.getState = getState;
    // Global AmEx footer is missing required classes in non-US locales which results in a mostly unstyled footer.
    // Adding required classes for the locales where they're missing.
    const oneNavfooterElem = document.querySelector(
      '[data-module-name="axp-footer"]'
    );
    const oneNavFooterWrapper = oneNavfooterElem.parentElement;
    oneNavFooterWrapper.classList.add(
      'axp-footer__dls-module__module___1_EeR',
      'noindex'
    );
    oneNavFooterWrapper.classList.remove('pad-4');
    // `axp-footer__dls-module__module___1_EeR`
  }
  try {
    const resolution = await Promise.allSettled([
      dispatch(
        composeModules([
          { name: 'axp-global-header' },
          { name: 'axp-footer' },
          { name: 'axp-one-seo' },
        ])
      ),
      dispatch(
        loadOneCmsLocaleAirports(
          ownProps?.params?.locale ||
            ownProps?.location?.query?.locale ||
            'en-US'
        )
      ),
      dispatch(loadOneCmsLocaleCommonContent),
      dispatch(loadOneCmsLocaleProductContent(ownProps?.params?.product)),
      dispatch(isAuthenticatedAndLoadWallet(fetchClient)),
      dispatch(prefetchExperimentsV2()),
      dispatch(
        loadLanguagePack('travel-lounge-finder-root', {
          fallbackLocale: 'en-US',
        })
      ),
    ]);
    await Promise.all(
      resolution
        .filter((result) => result.status === 'rejected')
        .map(async (result) =>
          dispatch(
            addErrorToReport(result.reason, {
              collectionMethod: 'loadModuleData',
              moduleID: 'travel-lounge-finder-root',
              caught: true,
            })
          )
        )
    );
  } catch (e) {
    await dispatch(
      addErrorToReport(e, {
        collectionMethod: 'loadModuleData',
        moduleID: 'travel-lounge-finder-root',
        caught: false,
      })
    );
  }
};

TravelLoungeFinderRoot.holocron = {
  name: 'travel-lounge-finder-root',
  loadModuleData,
  reducer: combineReducers({
    auth: authReducer,
    lounge: loungeReducer,
    wallet: walletReducer,
    fetchye: OneCache().reducer,
    experimentConfig: experimentConfigReducer,
  }),
};

export default oneAppModuleWrapper('travel-lounge-finder-root')(
  TravelLoungeFinderRoot
);
